/**
 * Home health demo store — process-wide instance.
 *
 * Created lazily on first use: hydrate from localStorage or build a seed for the
 * wall-clock date in the branch zone until the tenant says its own; persist
 * (debounced) on every change; follow other tabs through the `storage` event.
 */
import { DEFAULT_CONFIG } from "@caredna-marlin/utils/homeHealthDemo/fixtures/config";
import { toDateKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { createDebouncedSaver, loadPersisted, savePersisted, subscribeToExternalChanges } from "./persistence";
import { buildSeed } from "./seed";
import type { TenantSeed } from "../engine/tenantDemoDay";
import type { DemoState } from "@caredna-marlin/utils/homeHealthDemo/state";
import { createDemoStore, type DemoStore } from "./store";

let instance: DemoStore | undefined;
let detach: (() => void) | undefined;

/**
 * **The tenant's demo day and seed stamp, once the horizon has read them; the
 * wall clock otherwise.**
 *
 * `HorizonFetcher` sets this from the Demo day marker on every poll, and when the
 * stamp differs from the one the store was built for it rebuilds the store from
 * `freshSeed`. A tenant seeded the night before used to disagree with the
 * browser by a day, and every screen that reads `demoToday` quietly answered a
 * different question from the one the data could answer — see
 * `engine/tenantDemoDay.ts` for the four times that cost us.
 *
 * Module state rather than store state, because `freshSeed` is what *builds* the
 * store and cannot read it.
 */
let tenant: TenantSeed | undefined;

export function freshSeed(now: Date = new Date()): DemoState {
  const demoToday = tenant?.day ?? toDateKey(now, DEFAULT_CONFIG.branch.timeZone);
  const seed = buildSeed({ demoToday, nowIso: now.toISOString() });
  return { ...seed, meta: { ...seed.meta, tenantStamp: tenant?.stamp } };
}

/**
 * **Follow the tenant's seed.** Remembers the tenant's demo day and stamp, and
 * rebuilds the store clean when the stamp differs from the one it was built for:
 * the tenant under it has been reseeded, however many times a day. No seed (no
 * marker yet, or a tenant from before the marker) changes nothing. Returns true
 * when the store was rebuilt.
 */
export function followTenantSeed(seed: TenantSeed | undefined, store: DemoStore = getDemoStore(), now: Date = new Date()): boolean {
  if (!seed) return false;
  tenant = seed;
  if (store.getState().meta.tenantStamp === seed.stamp) return false;
  store.dispatch({ type: "TENANT_RESEEDED", seed: freshSeed(now) });
  return true;
}

export function getDemoStore(): DemoStore {
  if (instance) return instance;
  const store = createDemoStore(loadPersisted() ?? freshSeed());
  const saver = createDebouncedSaver(savePersisted);
  const unsubscribe = store.subscribe(() => saver.schedule(store.getState()));
  const unsubscribeExternal = subscribeToExternalChanges((state) => store.dispatch({ type: "HYDRATE_EXTERNAL", state }));
  detach = () => {
    unsubscribe();
    unsubscribeExternal();
    saver.flush();
  };
  instance = store;
  return store;
}

export function resetDemoStore(store: DemoStore = getDemoStore(), now: Date = new Date()): void {
  store.dispatch({ type: "RESET", seed: freshSeed(now) });
}

/** Test hook: drop the singleton so the next `getDemoStore()` rebuilds it. */
export function __resetDemoStoreInstanceForTests(): void {
  detach?.();
  detach = undefined;
  instance = undefined;
  tenant = undefined;
}
